import { TextField, InputAdornment, Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMemo } from "react";
import SearchIcon from "../../../icons/Search";
import Button from "../../../components/Button";
import { NoDataText } from "../../components/SectionComponents";
import ReplacementDiffModal from "./ReplacementDiffModal";
import { isItem } from "./specificationState";

const LookupRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const LookupField = styled(TextField)({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
  },
});

type ItemReplacementModalProps = {
  open: boolean;
  currentItem: ItemWithBOM;
  replacementItem: ItemWithBOM | null;
  lookupValue: string;
  isLookingUp: boolean;
  allowedTemplateUuids?: string[];
  // The replacement is already a sibling in the parent BOM.
  duplicateError?: string | null;
  onLookupValueChange: (value: string) => void;
  onLookup: () => void;
  onCancel: () => void;
  onConfirm: () => void;
};

const ItemReplacementModal = ({
  open,
  currentItem,
  replacementItem,
  lookupValue,
  isLookingUp,
  allowedTemplateUuids,
  duplicateError,
  onLookupValueChange,
  onLookup,
  onCancel,
  onConfirm,
}: ItemReplacementModalProps) => {
  const validationError = useMemo(() => {
    if (!replacementItem) {
      return null;
    }

    const samePartNumber =
      currentItem.item.arena_part_number ===
      replacementItem.item.arena_part_number;
    const sameUuid =
      isItem(currentItem.item) &&
      isItem(replacementItem.item) &&
      currentItem.item.uuid === replacementItem.item.uuid;

    if (samePartNumber || sameUuid) {
      return "Selected part number must be different from the current item.";
    }

    const currentTypeUuid =
      isItem(currentItem.item) && currentItem.item.item_type?.uuid
        ? currentItem.item.item_type.uuid
        : null;
    const replacementTypeUuid =
      isItem(replacementItem.item) && replacementItem.item.item_type?.uuid
        ? replacementItem.item.item_type.uuid
        : null;
    const currentTypeName = currentItem.item.item_type_name
      ? currentItem.item.item_type_name.toLowerCase()
      : null;
    const replacementTypeName = replacementItem.item.item_type_name
      ? replacementItem.item.item_type_name.toLowerCase()
      : null;

    if (currentTypeUuid && replacementTypeUuid) {
      if (currentTypeUuid !== replacementTypeUuid) {
        return "Selected item type must match the current item type.";
      }
    } else if (currentTypeName && replacementTypeName) {
      if (currentTypeName !== replacementTypeName) {
        return "Selected item type must match the current item type.";
      }
    } else if (
      currentTypeUuid ||
      replacementTypeUuid ||
      currentTypeName ||
      replacementTypeName
    ) {
      return "Selected item type must match the current item type.";
    }

    if (duplicateError) {
      return duplicateError;
    }

    if (!allowedTemplateUuids || allowedTemplateUuids.length === 0) {
      return null;
    }

    const replacementTemplateUuids = isItem(replacementItem.item)
      ? [
          replacementItem.item.base_template?.template_definition_uuid,
          replacementItem.item.base_template?.uuid,
        ].filter((uuid): uuid is string => Boolean(uuid))
      : [];

    const isAllowed = replacementTemplateUuids.some((uuid) =>
      allowedTemplateUuids.includes(uuid)
    );

    if (!isAllowed) {
      return "Selected item template is not allowed for this parent item.";
    }

    return null;
  }, [currentItem, replacementItem, allowedTemplateUuids, duplicateError]);

  const effectiveReplacementItem = validationError ? null : replacementItem;
  const lookupContent = (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="body2" color="textSecondary">
        Search for a new part number to replace current
      </Typography>
      <LookupRow>
        <LookupField
          placeholder="Part number"
          size="small"
          value={lookupValue}
          onChange={(event) => onLookupValueChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              onLookup();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          color="primary"
          onClick={onLookup}
          disabled={!lookupValue.trim() || isLookingUp}
        >
          LOOK-UP PN
        </Button>
      </LookupRow>
      {validationError ? (
        <Typography variant="body2" sx={{ color: "#d32f2f" }}>
          {validationError}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <ReplacementDiffModal
      open={open}
      title="Part Number Replacement"
      currentItem={currentItem}
      replacementItem={effectiveReplacementItem}
      preDiffContent={lookupContent}
      emptyState={
        <NoDataText>Part number information will be loaded here</NoDataText>
      }
      confirmDisabled={Boolean(validationError)}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
};

export default ItemReplacementModal;
